<i18n>
{
	"en": {
		"title": "Select transporter",
		"edipartName": "EDIPART",
		"sender": "Sender",
		"recipient" : "Recipient",
		"messageType" : "MessageType",
		"chooseTransporter": "Select a transporter for orders: {orderNumbers}",
		"addToTransporter": "Add to Transporter",
		"fromPickupLocation": "From Pickup Location",
		"toDeliveryLocation": "To Delivery Location",
		"multipleLocations": "Multiple Locations",
		"plannedETD": "Planned Departure",
		"plannedETA": "Planned Arrival",
		"selectDate": "Select date",
		"customFromLocation": "Custom Loading Location",
		"customToLocation": "Custom Unloading Location",
		"useCustomFromLocation": "Use custom loading location",
		"useCustomToLocation": "Use custom unloading location",
		"edipartDetails1": "Transporter setup on EDI",
		"edipartDetails2": "Click to view details",
		"serviceCode": "Service codes",
		"someOrdersHasValidationErrors" : "Some orders have validation errors. Check table below and correct them before proceeding.",
		"sendEmail": "Send transport instruction by email",
		"emailFreeText": "Email free text",
		"emailAddrToUse": "Transporters email address",
		"updateEmail": "Update transporter email",
		"emailUpdateFailed": "Transporter email update failed",
		"emailUpdateSuccess": "Oppdatering av transportørens email ble gjennomført",
		"validationErrors": "View validation errors"
	},
	"no": {
		"title": "Velg transportør",
		"edipartName": "EDIPART",
		"sender": "Sender",
		"recipient" : "Mottaker",
		"messageType" : "Meldings type",
		"chooseTransporter": "Velg en transportør for oppdrag: {orderNumbers}",
		"addToTransporter": "Legg til på transportør",
		"multipleLocations": "Flere steder",
		"fromPickupLocation": "Fra hentested",
		"toDeliveryLocation": "Til leveringssted",
		"plannedETD": "Planlagt Avgang",
		"plannedETA": "Planlagt Ankomst",
		"selectDate": "Velg dato",
		"customFromLocation": "Egendefinert lastested",
		"customToLocation": "Egendefinert lossested",
		"useCustomFromLocation": "Bruk egendefinert lastested",
		"useCustomToLocation": "Bruk egendefinert lossested",
		"edipartDetails1": "Transportør satt opp på EDI",
		"edipartDetails2": "Trykk for å se detaljer",
		"serviceCode": "Service koder",
		"someOrdersHasValidationErrors" : "Noen oppdrag har valideringsfeil på seg. Korriger disse feilene før du forsetter.",
		"sendEmail": "Send transportinstruksjon via email",
		"emailFreeText": "Fritekst email",
		"emailAddrToUse": "Transportørens email-adresse",
		"updateEmail": "Oppdater transportørens email",
		"emailUpdateFailed": "Oppdatering av transportørens email feilet",
		"emailUpdateSuccess": "Oppdatering av transportørens email ble gjennomført",
		"validationErrors": "Se valideringsfeil"
	}
}
</i18n>
<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('title')"
		icon="order"
		iconColor="blue"
		width="660px"
		@close="onClose"
		@shown="processShown"
	>
		<div class="dialog-top">
			{{ $t('chooseTransporter', {orderNumbers}) }}
		</div>
		<div class="dialog-mid">
			<div class="dialog-left">
				<t-form-group>
					<vendor-select
						:selectedItem="vendor"
						:filterPostalCodes="filterPostalCodes"
						preferredFirst
						width="100%"
						style="margin-bottom:10px"
						@change="setVendor"
					/>
				</t-form-group>
				<div v-if="edipart" class="edipart">
					<label>{{$t('edipartDetails1')}}</label>
					<label>{{$t('edipartDetails2')}}</label>
					<t-button
						style="margin-top: 1em;margin-bottom: 1em"
						appearance="minimal"
						@click="showEdipartDetails"
					> {{edipart ? edipart.name : ''}}</t-button>

					<EdipartDetailsDialog
						:isShown="viewEdipartDetails"
						:edipart="edipart"
						:vendor="vendor"
						@close="viewEdipartDetails=false"
					/>
					<label>{{$t('serviceCode')}}</label>
					<t-select
						:disabled="edipart.outboundSetup.serviceCodes.length === 0"
						:selectedItem="getSelectedItem()"
						:items="serviceCodes"
						@change="setServiceCode($event)"
					>
						<template v-slot:displayedValue>
							{{getServiceCodeDisplayValue}}
						</template>

						<template v-slot:optionItemValue="slotProps">
							{{ getServiceCodeDisplayValueForItems(slotProps.item)}}
						</template>
					</t-select>
				</div>

				<t-form-group :title="$t('plannedETD')">
					<div style="display:flex; margin-bottom:10px">
						<t-date-picker
							:placeholder="$t('selectDate')"
							:value="displayDateFormatter(etd.date)"
							:dateFormat="displayDateFormat"
							style="margin-right: 5px;"
							@input="changeEtd({dateValue: $event, hasTime: null})"
						/>
						<t-time-picker
							:value="etdTime"
							@input="changeEtd({timeValue: $event, hasTime: Boolean($event)})"
						/>
					</div>
				</t-form-group>
				<t-form-group :title="$t('plannedETA')">
					<div style="display:flex">
						<t-date-picker
							:placeholder="$t('selectDate')"
							:value="displayDateFormatter(eta.date)"
							:dateFormat="displayDateFormat"
							style="margin-right: 5px;"
							@input="changeEta({dateValue: $event, hasTime: null})"
						/>

						<t-time-picker
							:value="etaTime"
							@input="changeEta({timeValue: $event, hasTime: Boolean($event)})"
						/>
					</div>
				</t-form-group>
				<div class="custom-switches">
					<t-switch
						v-model="allowCustomFrom"
						:label="$t('useCustomFromLocation')"
						@change="customFromToggle"
					/>
					<t-switch
						v-model="allowCustomTo"
						:label="$t('useCustomToLocation')"
						@change="customToToggle"
					/>
					<t-switch
						v-model="sendEmail"
						:label="$t('sendEmail')"
					/>
					<t-text-input
						v-model="vendorEmail"
						:disabled="!sendEmail"
						:label="$t('emailAddrToUse')"
					/>
					<t-button
						:disabled="!sendEmail || emailIsUpToDate"
						appearance="primary"
						@click="onUpdateVendorEmail"
					>
						{{ $t('updateEmail') }}
					</t-button>
					<t-text-area
						v-model="emailFreeText"
						:disabled="!sendEmail"
						:label="$t('emailFreeText')"
					/>

					<t-button
						v-if="hasValidationErrors"
						appearance="minimal"
						intent="danger"
						@click="viewValidationResults = true"
					>
						{{$t('validationErrors')}}
					</t-button>
				</div>
			</div>
			<div class="dialog-right">
				<t-stretch-select
					v-if="fromLocations && toLocations"
					:fromLocations="fromLocations"
					:fromIndex="from"
					:toLocations="toLocations"
					:toIndex="to"
					:allowCustomFrom="allowCustomFrom"
					:allowCustomTo="allowCustomTo"
					@changeFromLocation="changeFromLocation"
					@changeToLocation="changeToLocation"
				>
					<template v-slot:customFrom>
						<GlobalAddressInput
							:title="$t('customFromLocation')"
							:value="customFromLocation"
							@change="onCustomFromSelected"
						/>
					</template>
					<template v-slot:customTo>
						<GlobalAddressInput
							:title="$t('customToLocation')"
							:value="customToLocation"
							@change="onCustomToSelected"
						/>
					</template>
				</t-stretch-select>
			</div>
			<ValidationErrorsDialog
				:isShown="viewValidationResults"
				:validationResults="validationResults"
				@closeDialog="viewValidationResults = false"
			/>
			
		</div>

		<template v-slot:footer>
			<t-button
				:disabled="addDisabled"
				appearance="primary"
				class="timpex-modal-button"
				width="100%"
				@click="addToTransporter"
			>
				{{$t('addToTransporter')}}
			</t-button>
		</template>
	</t-dialog>
</template>
<script>

import {
	displayDateFormat, storageDateFormat, createDateObject, displayDateFormatter
} from '@/core';
import VendorSelect from '@/modules/vendors/VendorSelect';
import moment from 'moment';
import deepCopy from 'deepcopy';
import getEdipartDetailsRequest from './edi/getEdipartDetailsRequest';
import EdipartDetailsDialog from './edi/EdipartDetailsDialog.vue';
import getValidationResultFromSelectedOrders from './edi/getValidationResultFromSelectedOrders';
import ValidationErrorsDialog from './edi/ValidationErrorsDialog.vue';
import { mapActions } from '@/modules/vendors/store/vendors.store';
import GlobalAddressInput from '@/modules/globalAddresses/GlobalAddressInput';
import TStretchSelect from '@/components/evergreen/timpex/inputs/TStretchSelect';

export default {
	name: 'AddOrderToTransporterSelect',
	components: { TStretchSelect, GlobalAddressInput, VendorSelect, EdipartDetailsDialog, ValidationErrorsDialog },
	props: {
		isShown: Boolean,
		orders: Array,
		selectedTransporterOrder: Object,
		disableSubmit: Boolean,
	},
	data() {
		return {
			vendor: null,
			fromLocations: [],
			viewEdipartDetails: false,
			toLocations: [],
			customFromLocation: {},
			customToLocation: {},
			edipart: null,
			from: 0,
			to: 0,
			etd: {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			},
			eta: {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			},
			allowCustomFrom: false,
			allowCustomTo: false,
			validationResults: [],
			serviceCodes: [],
			currentServiceCode: undefined,
			vendorEmail: '',
			sendEmail: false,
			emailFreeText: '',
			viewValidationResults: false,
		};
	},

	computed: {
		edipartIdDefinedOnTransporter() {
			return this.vendor && Boolean(this.vendor.edipartId);
		},
		emailDefinedOnTransporter() {
			return this.vendor && Boolean(this.vendor.email);
		},
		emailIsUpToDate() {
			return this.vendor && this.vendor.email === this.vendorEmail;
		},

		getServiceCodeDisplayValue() {
			if (this.currentServiceCode && this.currentServiceCode.internalCode)
				return `${this.currentServiceCode.internalCode  } (${this.currentServiceCode.externalCode})`;
			else if (this.currentServiceCode && !this.currentServiceCode.internalCode)
				return '';
			return '';
		},
		displayDateFormat() {
			return displayDateFormat;
		},
		addDisabled() {
			return !this.vendor || !this.etd || this.disableSubmit || this.hasValidationErrors;
		},
		orderNumbers() {
			if (!this.orders) return '';
			return this.orders.map(x => x.orderNumber).join(', ');
		},
		etdTime() {
			if (!this.etd.hasTime) return null;

			return this.etd.date ?
				moment(this.etd.date, storageDateFormat).format('LT') :
				undefined;
		},
		etaTime() {
			if (!this.eta.hasTime) return null;

			return this.eta.date ?
				moment(this.eta.date, storageDateFormat).format('LT') :
				undefined;
		},

		hasValidationErrors() {
			return this.validationResults.some(x => x.validationErrors.length > 0);
		},
		filterPostalCodes() {
			let result = [];
			this.orders.forEach( x => {
				if (x.despatchParty && x.despatchParty.postalCode) {
					result.push(x.despatchParty.postalCode);
				}
			});
			return result;
		}
	},
	methods: {
		...mapActions(['updateVendorEmail']),
		displayDateFormatter,
		processShown() {
			if (this.selectedTransporterOrder && this.selectedTransporterOrder.vendor && this.selectedTransporterOrder.type.internalCode === 'external') {
				this.vendor = deepCopy(this.selectedTransporterOrder.vendor);
				this.vendorEmail = this.vendor.email;
				this.handleTransporterEdipart();
			} else {
				this.vendor = null;
				this.vendorEmail = '';
			}

			this.fromLocations = [];
			this.toLocations = [];

			if (this.orders && this.orders.length > 0) {
				let despatchParty = this.orders[0].despatchParty || this.orders[0].consignor;
				if (despatchParty) {
					let fromName = this.orders.length > 1 ? this.$t('multipleLocations') : despatchParty.name;
					this.fromLocations.push({
						label: this.$t('fromPickupLocation'),
						labelClass: 'stretch-details-label',
						primaryClass: 'stretch-details-primary',
						name: fromName,
						option: 'despatchParty'
					});
				}
				let deliveryParty = this.orders[0].deliveryParty || this.orders[0].consignee;
				if (deliveryParty) {
					let toName = this.orders.length > 1 ? this.$t('multipleLocations') : deliveryParty.name;
					this.toLocations.push({
						label: this.$t('toDeliveryLocation'),
						labelClass: 'stretch-details-label',
						primaryClass: 'stretch-details-primary',
						name: toName,
						option: 'deliveryParty'
					});
				}
				this.etd = deepCopy(this.orders[0].etd);
				this.eta = deepCopy(this.orders[0].eta);
			}

			this.changeFromLocation(0);
			this.changeToLocation(0);
		},

		getSelectedItem() {
			if (this.vendor.serviceCode)
				this.currentServiceCode = this.serviceCodes.find(x => x.internalCode === this.vendor.serviceCode.internalCode);
			else
				this.currentServiceCode = this.serviceCodes[0];
		},
		onClose() {
			this.$emit('close');
			this.resetWindow();
		},
		setServiceCode(item) {
			this.currentServiceCode = item;
		},
		resetWindow() {
			this.vendor = null;
			this.fromLocations = [];
			this.viewEdipartDetails = false;
			this.toLocations = [];
			this.customFromLocation = {};
			this.customToLocation = {};
			this.edipart = null;
			this.from = 0;
			this.to = 0;
			this.etd = {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			};
			this.eta = {
				date: '',
				hasTime: false,
				timeZone: 'Europe/Oslo',
				epoch: ''
			};
			this.allowCustomFrom = false;
			this.allowCustomTo = false;
			this.validationResults = [];
		},
		addToTransporter() {
			let despatchOption = this.from >= this.fromLocations.length ? 'custom' : this.fromLocations[this.from].option;
			let deliveryOption = this.to >= this.toLocations.length ? 'custom' : this.toLocations[this.to].option;
			this.$emit('addToTransporter',{
				vendor: { ...this.vendor, email: this.vendorEmail },
				despatchOption: despatchOption,
				deliveryOption: deliveryOption,
				etd: this.etd,
				eta: this.eta,
				customDespatchParty: this.customFromLocation,
				customDeliveryParty: this.customToLocation,
				serviceCode: this.currentServiceCode ? this.currentServiceCode.internalCode : undefined,
				sendEmail: this.sendEmail,
				freeText: this.emailFreeText,
			});
		},
		setVendor(value) {
			this.vendor = value;
			this.vendorEmail = value.email;
			this.handleTransporterEdipart();
			if (value.edipartId) this.sendEmail = false;
			else this.sendEmail = true;
		},

		handleTransporterEdipart() {
			if (this.vendor.edipartId) {
				this.getEdipartDetails();
				this.validationResults = getValidationResultFromSelectedOrders(this.orders);
				if (this.hasValidationErrors)
					this.$notify({
						type: 'error',
						title: this.$t('someOrdersHasValidationErrors')
					});
			} else
				this.edipart = null;
		},
		async getEdipartDetails() {
			this.edipart = await getEdipartDetailsRequest(this.vendor.edipartId);
			this.serviceCodes = [{ internalCode: null, externalCode: null }];
			this.serviceCodes = this.serviceCodes.concat(this.edipart.outboundSetup.serviceCodes);
		},
		changeFromLocation(index) {
			this.from = index;
		},
		changeToLocation(index) {
			this.to = index;
		},
		onCustomFromSelected(value) {
			this.customFromLocation = value;
		},

		getServiceCodeDisplayValueForItems(item) {
			if (item.internalCode)
				return `${item.internalCode  } (${item.externalCode})`;
			return '';
		},
		onCustomToSelected(value) {
			this.customToLocation = value;
		},
		customFromToggle(value) {
			if (value) {
				this.changeFromLocation(this.fromLocations.length);
			} else {
				this.changeFromLocation(0);
			}
		},
		customToToggle(value) {
			if (value) {
				this.changeToLocation(this.toLocations.length);
			} else {
				this.changeToLocation(0);
			}
		},
		changeEtd({ dateValue, timeValue, hasTime }) {
			let storedDate = this.etd ? this.etd.date : undefined;
			let date = dateValue ? dateValue : storedDate;

			let time = timeValue ?
				moment(timeValue, 'LT').format('HH:mm') :
				moment(storedDate, storageDateFormat).format('HH:mm');

			hasTime = hasTime === null ?
				this.etd.hasTime :
				hasTime;

			this.etd = createDateObject({ date, hasTime, time }, Boolean(dateValue));
		},
		showEdipartDetails() {
			this.viewEdipartDetails = true;
		},
		changeEta({ dateValue, timeValue, hasTime }) {
			let storedDate = this.eta ? this.eta.date : undefined;
			let date = dateValue ? dateValue : storedDate;

			let time = timeValue ?
				moment(timeValue, 'LT').format('HH:mm') :
				moment(storedDate, storageDateFormat).format('HH:mm');

			hasTime = hasTime === null ?
				this.eta.hasTime :
				hasTime;

			this.eta = createDateObject({ date, hasTime, time }, Boolean(dateValue));
		},

		async onUpdateVendorEmail() {
			try {
				let result = await this.updateVendorEmail({
					vendor: this.vendor,
					newEmail: this.vendorEmail,
				});
				this.vendor = result;
				this.$notify({
					type: 'success',
					title: this.$t('emailUpdateSuccess'),
				});
			} catch (err) {
				this.$notify({
					type: 'error',
					title: this.$t('emailUpdateFailed'),
				});
			}
		},
	},
};
</script>

<style >
.stretch-details-label {
	font-size: 11px;
	font-weight: 500;
}
.stretch-details-primary {
	font-size: 12px;
}
</style>

<style lang="scss" scoped>

.edipart {
	display: grid;
	margin-top:1em;
	margin-bottom: 1em;
	font-size: 11px;
	font-weight: 500;
}
.dialog-mid {
	display: grid;
	column-gap: 20px;
	grid-template-columns: 225px auto;
	height: 750px;
	margin-top:5px;
	padding-bottom: 20px;

	.custom-switches {
		display: grid;
		grid-row-gap: 10px;
		grid-template-rows: repeat(5, min-content);
		padding-top:20px;
	}
}
</style>

